// src/components/ShareDialog.jsx
// Share a tournament with its participants through a read-only link, with a QR code.
import React, { useEffect, useRef, useState } from 'react';
import QrCode from './QrCode';
import { useConfirm } from './ConfirmDialog';
import { useToast } from './Toast';
import { publicOrigin } from '../native';

export const shareUrl = (token) => `${publicOrigin()}/s/${token}`;

// onStart() creates the link, onStop() ends it; both resolve truthy on success.
function ShareDialog({ name, token, onStart, onStop, onClose }) {
    const confirm = useConfirm();
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const url = token ? shareUrl(token) : '';
    const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const start = async () => {
        setBusy(true);
        await onStart();
        setBusy(false);
    };

    const stop = async () => {
        const confirmed = await confirm({
            title: 'Stop sharing?',
            message: 'The link stops working for everybody who has it. Sharing again creates a new link.',
            confirmLabel: 'Stop sharing',
        });
        if (!confirmed) return;
        setBusy(true);
        await onStop();
        setBusy(false);
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch (error) {
            inputRef.current?.select(); // no clipboard access: leave the link selected to copy by hand
            toast.info('Press Ctrl+C (or copy) to copy the selected link.');
            return;
        }
        toast.success('Link copied.');
    };

    const shareWithApps = () => {
        navigator.share({ title: name, text: `Ranking and rounds of ${name}`, url }).catch(() => {});
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal share-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="share-title"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id="share-title">Share {name}</h2>
                <p className="muted">
                    Anyone with the link can watch the ranking and rounds live, and see the player names. They cannot change
                    anything.
                </p>

                {token ? (
                    <>
                        <div className="share-url">
                            <input
                                ref={inputRef}
                                className="input"
                                readOnly
                                aria-label="Share link"
                                value={url}
                                onFocus={(event) => event.target.select()}
                            />
                            <button className="btn btn-primary" onClick={copy}>
                                Copy link
                            </button>
                        </div>
                        <div className="share-qr">
                            <QrCode value={url} label={`QR code for the link to ${name}`} />
                            <p className="hint">Scan with a phone camera to open it.</p>
                        </div>
                        <div className="modal-actions share-actions">
                            <button className="btn btn-ghost btn-danger-text" onClick={stop} disabled={busy}>
                                Stop sharing
                            </button>
                            {canShare && (
                                <button className="btn" onClick={shareWithApps}>
                                    Share…
                                </button>
                            )}
                            <a className="btn" href={url} target="_blank" rel="noopener noreferrer">
                                Open
                            </a>
                            <a className="btn" href={`${url}/screen`} target="_blank" rel="noopener noreferrer" title="For a TV or projector at the venue">
                                Big screen
                            </a>
                            <button className="btn" onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="modal-actions">
                        <button className="btn" onClick={onClose}>
                            Cancel
                        </button>
                        <button className="btn btn-primary" onClick={start} disabled={busy}>
                            Create share link
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ShareDialog;
